// NOTE: NSAI-7215 — budgets for the Intelligent UI contract (```ui fences).
//
// Every number here is stated to the model in the prompt contract AND
// re-validated by the schema, the same arrangement the chart, diagram and map
// budgets live under. The server mirrors these values in
// apps/nsai/server/src/chat/constant/chat-system-prompts.constants.ts because it
// carries no workspace dependency on this package — change one side and you
// must change the other, or the model is told one limit and validated against
// another, and the block falls back to its source in the transcript.

import { EUiParseFailure, type TParseUiResult } from "../types/ui";

// NOTE: The fence language that marks a block as an interactive interface.
// Shared so web, mobile and the server's bot stripping agree on one spelling.
export const UI_FENCE_LANGUAGE = "ui" as const;

// NOTE: Six top-level blocks is already a long answer. Past that the reply
// stops being a message and becomes a page, which is what the ```html contract
// is for.
export const MAX_UI_BLOCKS = 6;
export const MAX_UI_TAB_BLOCKS = 4;

export const MAX_UI_INPUTS = 8;
export const MAX_UI_VALUES = 12;
export const MAX_UI_SELECT_OPTIONS = 8;

export const MAX_UI_STATS_ITEMS = 6;

export const MIN_UI_COMPARISON_OPTIONS = 2;
export const MAX_UI_COMPARISON_OPTIONS = 4;
export const MAX_UI_COMPARISON_ATTRIBUTES = 10;

export const MAX_UI_TABLE_COLUMNS = 8;
export const MAX_UI_TABLE_ROWS = 50;

export const MAX_UI_STEPS = 12;

export const MIN_UI_TABS = 2;
export const MAX_UI_TABS = 5;

export const MAX_UI_ACTIONS = 4;

// NOTE: A plot is drawn by the chart renderer, so it lives inside the chart
// budgets: at most 20 points on the x axis and 60 plotted cells in all. With
// more series, the points thin out to stay under the cell budget.
export const MAX_UI_PLOT_SERIES = 4;
export const MAX_UI_PLOT_POINTS = 20;
export const MAX_UI_PLOT_CELLS = 60;

export const MAX_UI_TITLE_LENGTH = 80;
export const MAX_UI_LABEL_LENGTH = 60;
export const MAX_UI_TEXT_LENGTH = 140;
// NOTE: NSAI-7841 — free text past `MAX_UI_TEXT_LENGTH` is kept whole and cut
// only on screen, its full wording one tap on the info icon away. This is the
// cap on what is kept: far past anything a model writes in one verdict or
// hint, so only a runaway field is refused.
export const MAX_UI_FULL_TEXT_LENGTH = 1000;
export const MAX_UI_CELL_LENGTH = 80;
export const MAX_UI_EXPRESSION_LENGTH = 200;
export const MAX_UI_ACTION_PROMPT_LENGTH = 500;
export const MAX_UI_ID_LENGTH = 32;

// NOTE: An upper bound on the work one expression may cost. A 200-character
// expression cannot get near it honestly; the bound exists so a pathological
// one (deep nesting, a plot over many points) is refused rather than freezing
// the message list on a phone.
export const MAX_UI_EXPRESSION_NODES = 120;
export const MAX_UI_EXPRESSION_DEPTH = 24;

// NOTE: The variable a plot series is written over: `y` is an expression of
// `x`, which walks from the plot's `from` to its `to`.
export const UI_PLOT_VARIABLE = "x" as const;

// NOTE: What a value that is not a finite number renders as — division by
// zero, the log of a negative. Never "NaN" or "Infinity", which read as a bug.
export const UI_NOT_A_NUMBER_TEXT = "—" as const;

// NOTE: Written like the schema's own messages, and fixed rather than taken
// from JSON.parse, whose wording differs between the browser engine and Hermes.
export const UI_INVALID_JSON_ERROR =
  "The interface is not valid JSON — check for a missing bracket, quote or comma";

// NOTE: NSAI-7852 — how many missing closing brackets `repairUiJson` may add
// to a finished block. A model that forgot one or two is repaired; an answer
// cut off mid-block is missing more than that and stays refused.
export const MAX_UI_JSON_ADDED_BRACKETS = 2;

// NOTE: NSAI-7852 — the bracket pairs `repairUiJson` balances.
export const UI_JSON_OPENER_BY_CLOSER: ReadonlyMap<string, string> = new Map([
  ["}", "{"],
  ["]", "["],
]);

export const UI_JSON_CLOSER_BY_OPENER: ReadonlyMap<string, string> = new Map([
  ["{", "}"],
  ["[", "]"],
]);

export const UI_JSON_WHITESPACE_PATTERN = /\s/;

// NOTE: NSAI-7841 — what ends free text (a verdict, a subtitle, a hint, a step
// detail) that `shortenUiText` cut to its budget.
export const UI_TEXT_ELLIPSIS = "…" as const;

// NOTE: NSAI-7841 — a number written as text ("1", "-0.5") that a numbers-only
// field still accepts. A formula, a unit or a word does not match.
export const UI_NUMERAL_PATTERN = /^-?\d+(\.\d+)?$/;

// NOTE: NSAI-7841 — the keys under which models put the option rows of a
// comparison they wrote in their own layout, in the order they are tried.
export const UI_COMPARISON_ROW_KEYS = ["options", "items", "rows"] as const;

// NOTE: NSAI-7841 — the keys that name an option in a row written in the
// model's own layout. A row's other text or number keys are its attributes.
export const UI_COMPARISON_NAME_KEYS = ["label", "name", "title"] as const;

// NOTE: NSAI-7841 — a first column header that names the options ("Plan",
// "Cloud service", "Provider"), so the first cell of each row is a name.
export const UI_COMPARISON_NAME_HEADER_PATTERN =
  /\b(plan|service|provider|option|name|product|platform|tool|company|brand|vendor)s?\b/i;

// NOTE: NSAI-7841 — code points that join the character before them into one
// displayed character: combining accents, a zero-width joiner (and so the
// emoji after it), text / emoji variation selectors, an emoji skin tone, the
// keycap mark and the tag characters of a subdivision flag. Cutting before
// one of them would change the character ("👍🏽" into "👍").
export const UI_JOINING_CODE_POINT_RANGES: readonly (readonly [
  number,
  number,
])[] = [
  [0x0300, 0x036f],
  [0x200d, 0x200d],
  [0x20e3, 0x20e3],
  [0xfe0e, 0xfe0f],
  [0x1f3fb, 0x1f3ff],
  [0xe0020, 0xe007f],
];

export const UI_ZERO_WIDTH_JOINER = "‍" as const;

// NOTE: NSAI-7841 — a flag is two regional indicators ("🇺" + "🇸"); half of
// one renders as a lone letter box.
export const UI_REGIONAL_INDICATOR_RANGE: readonly [number, number] = [
  0x1f1e6, 0x1f1ff,
];

// NOTE: NSAI-7841 — row keys that are neither the option's name nor one of its
// attributes, so a row with one key per attribute does not turn them into one.
export const UI_COMPARISON_NON_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  "id",
  "label",
  "name",
  "title",
  "sublabel",
  "description",
  "verdict",
  "recommended",
  "values",
  "attributes",
]);

// NOTE: NSAI-7841 — the id a converted comparison gives its Nth option.
export const UI_COMPARISON_OPTION_ID_PREFIX = "option_" as const;

// NOTE: NSAI-7852 — what a block that is not JSON, even after the repair,
// parses to. One shared value, so every refusal reads the same.
export const UI_INVALID_JSON_RESULT: TParseUiResult = {
  success: false,
  error: UI_INVALID_JSON_ERROR,
  failure: EUiParseFailure.INVALID_JSON,
};

// NOTE: Ids are referenced from expressions and from action prompts, so they
// must be valid expression identifiers — and never a word the grammar uses.
export const UI_ID_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const UI_RESERVED_IDENTIFIERS: ReadonlySet<string> = new Set([
  "true",
  "false",
  UI_PLOT_VARIABLE,
]);

// NOTE: `{id}` in an action prompt is replaced with the current value of that
// input or derived value when the button is pressed.
export const UI_ACTION_PLACEHOLDER_PATTERN = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

// NOTE: How finely a slider moves when the spec names no step: whole units on a
// wide range, a hundredth of the range on a narrow one (a 0–1 ratio would
// otherwise only ever be 0 or 1). Web and mobile read the same grid.
export const UI_SLIDER_DEFAULT_STEP = 1;
export const UI_SLIDER_FINE_RANGE = 10;
export const UI_SLIDER_FINE_DIVISIONS = 100;

// NOTE: Floating-point noise from stepping (0.1 + 0.2) is rounded away at this
// many decimals, so a slider never reports 0.30000000000000004.
export const UI_STEP_PRECISION = 1e6;
